import { useState, useEffect, useCallback, useRef } from 'react'

interface VisualViewportState {
  height: number
  keyboardHeight: number
  isKeyboardOpen: boolean
}

/**
 * Hook to track the visual viewport height and detect mobile keyboard presence.
 * Uses the visualViewport API to detect when the keyboard opens/closes.
 * Includes iOS Safari specific handling.
 */
export function useVisualViewport(): VisualViewportState {
  const prevKeyboardOpenRef = useRef(false)
  // Tallest viewport height seen with the keyboard closed = the true full
  // height for the current orientation. Used to override iOS's stale
  // post-keyboard-close height (see updateViewport). Re-baselined on
  // orientation change via the window 'resize' handler.
  const fullHeightRef = useRef(window.visualViewport?.height ?? window.innerHeight)
  const [state, setState] = useState<VisualViewportState>(() => {
    const height = window.visualViewport?.height ?? window.innerHeight
    return {
      height,
      keyboardHeight: 0,
      isKeyboardOpen: false,
    }
  })

  const updateViewport = useCallback(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    const fullHeight = window.innerHeight
    const visibleHeight = viewport.height
    const heightDiff = fullHeight - visibleHeight
    // Detect the keyboard by visualViewport.height shrinking, NOT by
    // visualViewport.offsetTop. The offsetTop check used to be a belt-and-
    // suspenders OR-clause to catch the case where iOS auto-scrolls the
    // page to keep a low-positioned focused input visible. But on iOS PWA,
    // offsetTop is unreliable on the way out: after the keyboard closes via
    // swipe-down / Done / tap-outside, iOS sometimes leaves offsetTop at a
    // non-zero value, pinning isKeyboardOpen=true and clamping the page
    // height. The user then has to manually scroll for visualViewport.scroll
    // to fire and reset the state — visible as the input slightly stuck
    // higher than its pre-keyboard position. heightDiff alone reliably
    // covers genuine keyboards (always > 100 px) and reliably resets to 0
    // on close via the resize event.
    const isKeyboardOpen = heightDiff > 100
    const keyboardHeight = isKeyboardOpen ? Math.max(heightDiff, 0) : 0

    // Track the tallest viewport height seen with the keyboard closed —
    // that's the true full height for the current orientation. Re-baselined
    // on orientation change by the window 'resize' handler below.
    if (!isKeyboardOpen && visibleHeight > fullHeightRef.current) {
      fullHeightRef.current = visibleHeight
    }

    // iOS PWA bug, confirmed by debug-HUD readings: after a swipe-down
    // keyboard dismiss, BOTH window.innerHeight and visualViewport.height
    // stay pinned ~60 px below the true full height (e.g. 812 instead of
    // 874) until a *real* touch-scroll forces iOS to recompute. The HUD
    // showed sY/dT already at 0 in the stuck state, yet vH stayed at the
    // keyboard-open 812 — only the user's rubber-band pull-down restored
    // 874. A programmatic scroll can't trigger that recompute because the
    // workspace layout is exactly viewport-height (nothing to scroll, so
    // scrollTo is a no-op and no scroll event fires).
    //
    // So instead of coaxing iOS into recomputing, ignore its stale
    // post-close value and use the cached true full height directly. The
    // real screen IS that tall — iOS is just misreporting it. The `- 8`
    // tolerance avoids overriding for sub-pixel / rounding differences.
    const effectiveHeight =
      !isKeyboardOpen && visibleHeight < fullHeightRef.current - 8
        ? fullHeightRef.current
        : visibleHeight

    // iOS fires resize/scroll on every keyboard-animation frame. Bail out
    // when nothing changed so consumers (Workspace.tsx clamps page height
    // off this) don't re-render on every frame.
    setState((prev) =>
      prev.height === effectiveHeight &&
      prev.keyboardHeight === keyboardHeight &&
      prev.isKeyboardOpen === isKeyboardOpen
        ? prev
        : { height: effectiveHeight, keyboardHeight, isKeyboardOpen },
    )
  }, [])

  useEffect(() => {
    const viewport = window.visualViewport

    if (!viewport) {
      // Fallback for browsers without visualViewport API
      // Use focus/blur events as a heuristic
      const handleFocus = (e: FocusEvent) => {
        const target = e.target as HTMLElement
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          // Assume keyboard is open when input is focused
          setTimeout(() => {
            setState(prev => ({
              ...prev,
              isKeyboardOpen: true,
              keyboardHeight: 300, // Estimate
              height: window.innerHeight - 300,
            }))
          }, 300)
        }
      }

      const handleBlur = () => {
        setState({
          height: window.innerHeight,
          keyboardHeight: 0,
          isKeyboardOpen: false,
        })
      }

      document.addEventListener('focusin', handleFocus)
      document.addEventListener('focusout', handleBlur)

      return () => {
        document.removeEventListener('focusin', handleFocus)
        document.removeEventListener('focusout', handleBlur)
      }
    }

    // Use visualViewport API
    viewport.addEventListener('resize', updateViewport)
    viewport.addEventListener('scroll', updateViewport)

    // Window resize = orientation / window-size change (NOT keyboard — iOS
    // fires only visualViewport resize for the keyboard). If no input is
    // focused the keyboard can't be open, so the current height is the true
    // full height: re-baseline the cache. Without this, rotating to a
    // shorter viewport would be wrongly overridden by the taller previous
    // orientation's cached value.
    const onWindowResize = () => {
      const active = document.activeElement as HTMLElement | null
      const editing =
        !!active &&
        (active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.isContentEditable)
      if (!editing) {
        fullHeightRef.current = window.visualViewport?.height ?? window.innerHeight
      }
      updateViewport()
    }
    window.addEventListener('resize', onWindowResize)

    // iOS PWA quirk: after the keyboard closes via swipe-down / "Done" /
    // tap-outside, visualViewport.scroll doesn't always fire with the
    // offsetTop reset, so isKeyboardOpen (which checks `offsetTop > 50`)
    // stays stuck true. Without a URL bar to absorb the scroll offset
    // change — as happens in a regular browser tab — the stale offsetTop
    // is exposed directly, leaving the page-height clamp small and a
    // large empty area below the input.
    //
    // Force-resolve: when focus leaves the page entirely (or moves to a
    // non-editable element), the keyboard CAN'T be open, regardless of
    // what visualViewport reports. Defer ~300 ms so iOS finishes the
    // keyboard-close animation before we read window.innerHeight; that
    // delay is also short enough to feel instant relative to the user's
    // dismiss gesture.
    const onFocusOut = () => {
      window.setTimeout(() => {
        const active = document.activeElement as HTMLElement | null
        const stillEditing =
          !!active &&
          (active.tagName === 'INPUT' ||
            active.tagName === 'TEXTAREA' ||
            active.isContentEditable)
        if (stillEditing) return
        setState({
          height: window.innerHeight,
          keyboardHeight: 0,
          isKeyboardOpen: false,
        })
      }, 300)
    }
    document.addEventListener('focusout', onFocusOut)

    // Initial check
    updateViewport()

    return () => {
      viewport.removeEventListener('resize', updateViewport)
      viewport.removeEventListener('scroll', updateViewport)
      window.removeEventListener('resize', onWindowResize)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [updateViewport])

  // On the open → closed transition, clear any iOS document-scroll residue.
  //
  // When the keyboard opens with a low-positioned input focused, iOS can
  // auto-scroll the document up to keep the input visible, and that scroll
  // does NOT always auto-undo on close. HUD readings showed sY/dT at 0 in
  // most cases (so the actual "stuck higher" symptom is the viewport-height
  // bug handled by the cache override in updateViewport, NOT scroll) — but
  // this reset is cheap insurance for the cases where iOS does leave a
  // residual scroll offset.
  //
  // NOTE: we intentionally do NOT rely on a scroll round-trip to force iOS
  // to recompute its stale viewport height. That doesn't work here — the
  // workspace layout is exactly viewport-height, so scrollTo is a no-op and
  // fires no scroll event. The height correction lives in updateViewport.
  //
  // Only fire on the actual transition so we never disturb steady-state
  // scroll. None of the layouts that consume this hook scroll the body
  // intentionally — all body-level overflow is contained, and inner scroll
  // regions use their own overflow-y-auto containers — so forcing scrollY
  // to 0 here is safe across consumers.
  useEffect(() => {
    if (prevKeyboardOpenRef.current && !state.isKeyboardOpen) {
      window.scrollTo(0, 0)
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }
    prevKeyboardOpenRef.current = state.isKeyboardOpen
  }, [state.isKeyboardOpen])

  return state
}
